import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields, extractEntity, extractList as extractTournamentList } from '../utilities/tournamentsApi';
import { extractList, isAutoSettingConfigured } from '../utilities/disqualifications';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  country_id: '',
  league_id: '',
  enabled: true,
  disqualYC: '',
};

export default function ManageAutoDisqualification() {
  const { leagueId } = useParams();
  const isNew = !leagueId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('disqualifications');

  const [form, setForm] = useState(emptyForm);
  const [leagueName, setLeagueName] = useState('');
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryLeagues, setCountryLeagues] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCountryLeagues, setLoadingCountryLeagues] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const availableIds = useMemo(
    () => new Set(availableLeagues.map((item) => Number(item.id))),
    [availableLeagues]
  );

  const leaguesForCountry = useMemo(
    () => countryLeagues.filter((item) => availableIds.has(Number(item.id))),
    [countryLeagues, availableIds]
  );

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [leagueId, champId, champsLoading]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const loadCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const list = extractTournamentList(response.data).map((item) => ({
        id: item.id,
        name: item.name || item.country_name || '—',
      }));
      setCountries(list);
      return list;
    } catch (error) {
      setCountries([]);
      throw error;
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setCountryLeagues([]);
      return [];
    }
    setLoadingCountryLeagues(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const leagues = extractTournamentList(extractEntity(response.data).leagues).map((item) => ({
        id: item.id || item.league_id,
        name: item.name || item.league_name || '—',
      }));
      setCountryLeagues(leagues);
      return leagues;
    } catch (error) {
      setCountryLeagues([]);
      throw error;
    } finally {
      setLoadingCountryLeagues(false);
    }
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    if (!champId) {
      setLoading(false);
      setMessage({ text: t('dqLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      if (isNew) {
        const [settingsResponse, leaguesResponse] = await Promise.all([
          authorizedPost(dispatch, API_URLS.getAdminDisqualificationSettings, { champ_id: champId }),
          authorizedPost(dispatch, API_URLS.getAdminDisqualificationLeagues, { champ_id: champId }),
          loadCountries(),
        ]);
        const settings = extractList(settingsResponse.data);
        const leagues = extractList(leaguesResponse.data).filter((item) => Number(item.id) !== 0);
        const used = new Set(settings.filter(isAutoSettingConfigured).map((item) => Number(item.id)));
        setAvailableLeagues(leagues.filter((item) => !used.has(Number(item.id))));
        setCountryLeagues([]);
        setForm(emptyForm);
        setLeagueName('');
      } else {
        const settingsResponse = await authorizedPost(dispatch, API_URLS.getAdminDisqualificationSettings, {
          champ_id: champId,
        });
        const settings = extractList(settingsResponse.data);
        const current = settings.find((item) => Number(item.id) === Number(leagueId));
        if (!current) {
          setMessage({ text: t('dqNotFound'), type: 'error' });
          setForm(emptyForm);
          setLeagueName('');
        } else {
          setLeagueName(current.name || '');
          setForm({
            country_id: '',
            league_id: String(current.id),
            enabled: Number(current.disqualEnabled) === 1,
            disqualYC: current.disqualYC == null ? '' : String(current.disqualYC),
          });
        }
        setAvailableLeagues([]);
        setCountries([]);
        setCountryLeagues([]);
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('dqLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = async (countryId) => {
    setForm((prev) => ({
      ...prev,
      country_id: countryId,
      league_id: '',
    }));
    setErrors((prev) => ({ ...prev, country_id: '', league_id: '' }));
    setMessage({ text: '', type: '' });
    try {
      await loadLeaguesForCountry(countryId);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('tsLoadLeaguesError')), type: 'error' });
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (isNew && !form.country_id) nextErrors.country_id = t('twNeedCountry');
    if (isNew && !form.league_id) nextErrors.league_id = t('dqNeedTournament');
    const yc = Number(form.disqualYC);
    if (form.enabled && (!Number.isFinite(yc) || yc <= 0)) {
      nextErrors.disqualYC = t('dqNeedYC');
    } else if (form.disqualYC !== '' && (!Number.isFinite(yc) || yc < 0)) {
      nextErrors.disqualYC = t('dqNeedYC');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const nextLeagueId = Number(isNew ? form.league_id : leagueId);
      await authorizedPost(dispatch, API_URLS.changeAdminDisqualificationSettings, {
        champ_id: champId,
        league_id: nextLeagueId,
        disqualEnabled: form.enabled ? 1 : 0,
        disqualYC: form.disqualYC === '' ? null : Number(form.disqualYC),
      });
      if (isNew) {
        history.push('/league_disqualifications');
      } else {
        setMessage({ text: t('dqSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('dqSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.removeAdminDisqualificationSettings, {
        champ_id: champId,
        league_id: Number(leagueId),
      });
      history.push('/league_disqualifications');
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('dqRemoveError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const canCreate = Boolean(form.country_id) && leaguesForCountry.length > 0;

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page dq-edit-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('dqTitle'), to: '/league_disqualifications' },
                  { label: isNew ? t('dqNewAuto') : t('dqEditAuto') },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('dqLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-edit-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('dqTitle'), to: '/league_disqualifications' },
                { label: isNew ? t('dqNewAuto') : (leagueName || t('dqEditAuto')) },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                <div className="st-field st-field-wide">
                  <label className="st-check">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(event) => updateField('enabled', event.target.checked)}
                    />
                    {t('dqEnabled')}
                  </label>
                </div>

                {isNew ? (
                  <>
                    <div className="st-field st-field-wide">
                      <label>{t('gmCountry')} <span className="st-required">*</span></label>
                      {loadingCountries ? (
                        <p className="st-hint">{t('dqLoading')}</p>
                      ) : countries.length ? (
                        <select
                          className={`st-select ${errors.country_id ? 'error' : ''}`}
                          value={form.country_id}
                          onChange={(event) => handleCountryChange(event.target.value)}
                        >
                          <option value="">{t('cmCountryPh')}</option>
                          {countries.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="st-hint">{t('twNoCountries')}</p>
                      )}
                      {errors.country_id ? <span className="st-field-error">{errors.country_id}</span> : null}
                    </div>

                    <div className="st-field st-field-wide">
                      <label>{t('dqLeague')} <span className="st-required">*</span></label>
                      {!form.country_id ? (
                        <p className="st-hint">{t('twPickCountryFirst')}</p>
                      ) : loadingCountryLeagues ? (
                        <p className="st-hint">{t('dqLoading')}</p>
                      ) : leaguesForCountry.length ? (
                        <select
                          className={`st-select ${errors.league_id ? 'error' : ''}`}
                          value={form.league_id}
                          onChange={(event) => updateField('league_id', event.target.value)}
                        >
                          <option value="">{t('twLeaguePh')}</option>
                          {leaguesForCountry.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="st-hint">{t('dqNoLeaguesInCountry')}</p>
                      )}
                      {errors.league_id ? <span className="st-field-error">{errors.league_id}</span> : null}
                    </div>
                  </>
                ) : (
                  <div className="st-field st-field-wide">
                    <label>{t('dqLeague')}</label>
                    <input className="st-input" value={leagueName} readOnly />
                  </div>
                )}

                <div className="st-field">
                  <label>{t('dqYCCount')} {form.enabled ? <span className="st-required">*</span> : null}</label>
                  <input
                    type="number"
                    min="1"
                    className={`st-input ${errors.disqualYC ? 'error' : ''}`}
                    value={form.disqualYC}
                    onChange={(event) => updateField('disqualYC', event.target.value)}
                  />
                  {errors.disqualYC ? <span className="st-field-error">{errors.disqualYC}</span> : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {canWrite ? (
        <div className={`st-actions ${!isNew ? 'st-actions-split' : ''}`}>
          {!isNew ? (
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving || deleting}
              onClick={() => setDeleteVisible(true)}
            >
              {t('delete')}
            </button>
          ) : <span />}
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving || (isNew && !canCreate)}
            onClick={handleSave}
          >
            <FiSave /> {saving ? t('dqLoading') : (isNew ? t('dqAddAuto') : t('stdSaveChanges'))}
          </button>
        </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('dqDeleteAutoTitle')}</div>
          <p>{t('dqDeleteAutoText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
